"use client"

import { useEffect, useRef } from "react"
import type { Subscription } from "@rails/actioncable"

import { getCable } from "@/lib/actionCable"
import { useDevAuth as useAuth } from "@/hooks/use-dev-auth"

export type ConfettiStyle = "raining" | "fireworks" | "cannons"

type ConfettiMessage = {
  style: ConfettiStyle
  celebration_message?: string
}

export function useConfettiCable(
  onConfetti: (style: ConfettiStyle, message?: string) => void,
  enabled: boolean = true
) {
  const { getToken } = useAuth()
  const subscriptionRef = useRef<Subscription | null>(null)
  const callbackRef = useRef(onConfetti)

  // Keep callback ref up to date without re-running the effect
  useEffect(() => {
    callbackRef.current = onConfetti
  }, [onConfetti])

  useEffect(() => {
    if (!enabled) return

    const setup = async () => {
      const token = await getToken()
      if (!token) return

      const cable = getCable(token)
      if (!cable) return

      subscriptionRef.current = cable.subscriptions.create(
        { channel: "ConfettiChannel" },
        {
          received: (data: ConfettiMessage) => {
            if (data?.style) {
              callbackRef.current(data.style, data.celebration_message)
            }
          },
        }
      )
    }

    setup()

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }
    }
  }, [enabled, getToken])
}
